// @flow

import ThreeDSecureMode from '@tectonics/prs/threedsecure/threedsecure-mode';
import type { Merchant } from '@tectonics/prs/merchant';
import ThreeDSecureModeDecisionRule from '@tectonics/prs/threedsecure/threedsecure-mode-decision-rule';
import { getMerchant, getTokenizedCard } from '@tectonics/prs/incoming-payment';
import { getBrand, isValidTokenizedCard } from '@tectonics/prs/card/tokenized';
import type { IncomingPayment } from '@tectonics/prs/incoming-payment';
import type { UserInputValues } from '@tectonics/prs/user-input-values';

export default class ThreeDSecureModeResolver
{
  static resolve(incomingPayment: IncomingPayment, userInputValues: UserInputValues): ThreeDSecureMode|null
  {
    const merchant:Merchant = getMerchant(incomingPayment);

    if (!merchant || !merchant.threedsecure || (!merchant.threedsecure.threeds_mode_decision_rules && !merchant.threedsecure.threeds_mode)) {
      return ThreeDSecureMode.FULL_AUTHENTICATION;
    }

    if (merchant.threedsecure.threeds_mode) {
      return merchant.threedsecure.threeds_mode;
    }

    const paymentType:string|null = this.resolvePaymentTypeOrNull(userInputValues);
    const brand:string|null = this.resolveBrandOrNull(incomingPayment);
    const amount:number|null = this.resolveAmountOrNull(incomingPayment);

    for (const rule: ThreeDSecureModeDecisionRule of this.resolveDecisionRules(merchant)) {
      if (rule.isSatisfied(paymentType, brand, amount)) {
        return rule.mode;
      }
    }

    return null;
  }

  static resolvePaymentTypeOrNull(userInputValues: UserInputValues): string|null {
    return (userInputValues && userInputValues['selectedPaymentMethod'])
      ? userInputValues['selectedPaymentMethod']
      : null;
  }

  static resolveAmountOrNull(incomingPayment: IncomingPayment): number|null {
    return incomingPayment?.v2?.amount?.usd ?? null;
  }

  static resolveBrandOrNull(incomingPayment: IncomingPayment): string|null {
    if (incomingPayment.status === 'CA') {
      return null;
    }

    const tokenizedCard = getTokenizedCard(incomingPayment);
    if (!tokenizedCard || !isValidTokenizedCard(tokenizedCard)) {
      return null;
    }

    return getBrand(tokenizedCard) || null;
  }

  static resolveDecisionRules(merchant: Merchant): ThreeDSecureModeDecisionRule[] {

    let rules:ThreeDSecureModeDecisionRule[] = [];

    for (const ruleData of (merchant.threedsecure.threeds_mode_decision_rules || [])) {
      let rule:ThreeDSecureModeDecisionRule|null = ThreeDSecureModeDecisionRule.tryFromData(ruleData);
      if (rule !== null) {
        rules.push(rule);
      }
    }

    if (!minAmountExistOnRules(rules)) {
      return rules;
    }

    return rules.sort((a: ThreeDSecureModeDecisionRule, b: ThreeDSecureModeDecisionRule) => (b?.minAmountUsd ?? 0) - (a?.minAmountUsd ?? 0));
  }
}

function minAmountExistOnRules(rules: ThreeDSecureModeDecisionRule[]): boolean {
  return rules.some((rule) => rule.minAmountUsd);
}
